import React, { Fragment } from 'react'
import { Link } from 'react-router-dom'

// Matches /some-path or /some/nested/path (internal TCS routes)
const INTERNAL_PATH_PATTERN = /\/[a-z][a-z0-9\-/]*/g

/**
 * Maps known internal routes to descriptive screen-reader labels.
 *
 * Every new internal route referenced by inline chat links must have a
 * descriptive label here. Missing entries fall back to the raw path, which
 * degrades screen-reader clarity — callers hear "/some-path" instead of a
 * meaningful destination description. Add a new entry whenever a new TCS
 * route is added that may appear in AI chat message text.
 */
const INTERNAL_PATH_LABELS: Record<string, string> = {
  '/credentials': 'Connect a data source',
  '/credentials/create': 'Create a new credential',
  '/dashboards': 'Go to dashboards',
  '/workflows': 'Go to workflows',
  '/users': 'Go to users',
  '/billing': 'Go to billing',
  '/my-account': 'Go to my account',
  '/organizations': 'Go to organizations',
  '/support': 'Go to support',
}

// Matches http:// or https:// URLs (external links)
const EXTERNAL_URL_PATTERN = /https?:\/\/[^\s)]+/g

interface TextSegment {
  type: 'text' | 'internal' | 'external'
  value: string
}

/**
 * Strips markdown heading syntax (# h1, ## h2, etc.) from the start of each line.
 * Chat messages render plain text — the AI sometimes emits markdown heading markers.
 */
function stripMarkdownHeadings(text: string): string {
  return text.replace(/^#{1,6}\s+/gm, '')
}

/**
 * Parses a string and returns React nodes where:
 * - Markdown heading markers (# ## etc.) are stripped
 * - Internal paths like /credentials are rendered as React Router <Link> components
 * - External URLs (http:// or https://) are rendered as <a target="_blank"> tags
 * - All other content is rendered as plain text spans
 */
export function renderTextWithLinks(text: string): React.ReactNode[] {
  text = stripMarkdownHeadings(text)
  const segments: TextSegment[] = []

  // Build a combined regex that matches either external URLs or internal paths
  const combined = /https?:\/\/[^\s)]+|\/[a-z][a-z0-9\-/]*/g

  let lastIndex = 0
  let match: RegExpExecArray | null

  while ((match = combined.exec(text)) !== null) {
    const matchedValue = match[0]
    const matchStart = match.index

    // Trim trailing punctuation so URLs ending a sentence don't absorb the period/comma/etc.
    const trimmedValue = matchedValue.replace(/[.,!?;:'")\]}>]+$/, '')

    if (matchStart > lastIndex) {
      segments.push({ type: 'text', value: text.slice(lastIndex, matchStart) })
    }

    if (EXTERNAL_URL_PATTERN.test(trimmedValue)) {
      segments.push({ type: 'external', value: trimmedValue })
    } else if (INTERNAL_PATH_PATTERN.test(trimmedValue)) {
      segments.push({ type: 'internal', value: trimmedValue })
    } else {
      segments.push({ type: 'text', value: trimmedValue })
    }

    // Reset stateful regex lastIndex after .test() calls
    EXTERNAL_URL_PATTERN.lastIndex = 0
    INTERNAL_PATH_PATTERN.lastIndex = 0

    // Advance by trimmed length so trailing punctuation is re-processed as plain text
    lastIndex = matchStart + trimmedValue.length
  }

  if (lastIndex < text.length) {
    segments.push({ type: 'text', value: text.slice(lastIndex) })
  }

  return segments.map((segment, i) => {
    switch (segment.type) {
      case 'internal':
        return (
          <Link
            key={i}
            to={segment.value}
            className="text-accent underline text-sm font-medium hover:no-underline"
            aria-label={INTERNAL_PATH_LABELS[segment.value] ?? segment.value}
          >
            {segment.value}
          </Link>
        )
      case 'external':
        return (
          <a
            key={i}
            href={segment.value}
            target="_blank"
            rel="noopener noreferrer"
            className="text-accent underline text-sm font-medium hover:no-underline"
            aria-label={`Open external link: ${segment.value}`}
          >
            {segment.value}
          </a>
        )
      default:
        return <Fragment key={i}>{segment.value}</Fragment>
    }
  })
}
